<template>
  <div
    class="flow-manage flex column no-margin settings-css manage w-100"
    :class="{'announcement-space': showAnnouncement }" >
    <div
      v-if="!checkPermission('manage_settings') || forbidden" >
      <h1
        class="settings__title back-btn"
        @click="goBack('WAManage')" >
        <SBIcon
          class="mr-2"
          :icon="SBIconList.DOWN_ARROW"
          :color="SBColors.greyPrimary"
          :size="12"
          :rotate="90" />
        <p>Flow</p>
      </h1>
      <Forbidden />
    </div>

    <div
      v-else
      class="max-height" >
      <h1
        class="settings__title w-100 flex" >
        <div
          class="back-btn"
          @click="tryBack()">
          <SBIcon
            class="mr-2"
            :class="{ 'no-hover': editMode }"
            :icon="SBIconList.DOWN_ARROW"
            :color="SBColors.greyPrimary"
            :size="12"
            :rotate="90" />
          <p
            id="title-edit"
            ref="editableTitle"
            :class="{ 'edit-mode': editMode }"
            :contenteditable="editMode"
            @focus="titleEditFocus"
            @blur="titleEditBlur" >
            {{ title }}
          </p>
        </div>
        <SBIcon
          v-if="!editMode"
          class="edit-title mx-2"
          :icon="SBIconList.PENCIL_FULL"
          :color="SBColors.greyPrimary"
          :hover-icon-color="SBColors.blackPrimary"
          :size="8"
          :hover-size="18"
          pointer
          :rotate="0"
          @click="editTitle()"
          @click.native.prevent="" />
        <div
          v-if="!editMode"
          class="greedy" />
        <p
          v-if="!editMode"
          class="flow-state-label mr-2 no-shrink"
          :class="{ live: flowState }">{{ flowState ? 'Flow is turned ON' : 'Flow is turned OFF' }}</p>
        <SwitchCheckbox
          v-if="!editMode"
          v-model="flowState"
          size="medium no-shrink"
          class="no-label-margin"
          :off-outline-color="SBColors.blackUltralight"
          :value="true"
          @click.native.stop
          @input="activateFlow" />
      </h1>

      <Spinner
        v-if="preparingFlow"
        class="mt-5"
        :thickness="2" />

      <FlowTree
        v-else
        :flow="proccessedFlowData"
        :prevent-keys="editMode" />
    </div>

    <AddFlowNode />
    <FlowFrequencyModal
      ref="flowFrequencyModal" />
    <FlowNodeModal
      ref="flowNodeModal" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import Forbidden from '@/components/errors/Forbidden';
import ToasterMixin from '@/mixins/Toaster';
import CommonActions from '@/components/manage/CommonActions.mixin';
import UserPermissionsMixin from '@/mixins/UserPermissions.mixin';
import SwitchCheckbox from '@/components/common/SwitchCheckbox';
import Spinner from '@/components/common/Spinner';
import { memberTypes } from '@/helpers/enums/memberTypes.enum';
import { humanReadableTime } from '@/helpers/dateHelpers';
import FlowTree from './FlowTree';
import FlowFrequencyModal from './FlowFrequency.modal';
import FlowNodeModal from './FlowNode.modal';
import AddFlowNode from './AddFlowNode.modal';

export default {
  name: 'ManageFlow',
  components: {
    Forbidden,
    SwitchCheckbox,
    Spinner,
    FlowTree,
    FlowFrequencyModal,
    FlowNodeModal,
    AddFlowNode,
  },
  mixins: [
    ToasterMixin,
    UserPermissionsMixin,
    CommonActions,
  ],
  data() {
    return {
      forbidden: false,
      preparingFlow: true,
      proccessedFlowData: {},
      editMode: false,
      // Click on the back button should be delayed since the blur is triggered first and resets the edit mode
      backBtnDisabled: false,
      title: this.$route.params.flowName,
      flowState: this.$route.params.flowActive,
      splitBlocksCount: 0,
      blocksCount: 0,
    };
  },
  computed: {
    ...mapGetters({
      flowData: 'flows/flowData',
      flowsDefinitions: 'flows/flowsDefinitions',
      flowsDefinitionsUI: 'flows/flowsDefinitionsUI',
      errorUpdatingFlow: 'flows/errorUpdatingFlow',
      currentOrganisation: 'organisation/currentOrganisation',
      organisationMembers2: 'organisation/organisationMembers2',
      showAnnouncement: 'system/showAnnouncement',
    }),
  },
  watch: {
    flowData: {
      handler() {
        this.setScene();
      },
      deep: true,
    },
    errorUpdatingFlow: {
      handler() {
        if (this.errorUpdatingFlow) {
          try {
            this.showToaster(this.errorUpdatingFlow.data.message, null, null, 'error');
          } catch (error) {
            this.showToaster('Something went wrong. Please try again or contact support.', null, null, 'error');
          }
        }
      },
      deep: true,
    },
  },
  mounted() {
    if (this.emptyObject(this.flowsDefinitions)) {
      this.getFlowsDefinitions();
    }

    const titleElement = document.getElementById('title-edit');
    titleElement.addEventListener('paste', (e) => {
      // Prevent default so no styles are pasted
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      this.insertAtCaret(titleElement, text);
    });

    window.eventBus.$on('403', this.accessDenied);
    window.eventBus.$on('updateFrequency', this.updateFrequency);

    this.fetchFlowData({
      numberId: this.$route.params.numberId,
      flowId: this.$route.params.flowId,
    })
      .catch((error) => {
        if (error.response && error.response.data && error.response.data.message) {
          this.showToaster(error.response.data.message, null, null, 'error');
        } else {
          this.showToaster('Something went wrong. Please try again or contact support.', null, null, 'error');
        }
        this.$router.push({
          name: 'FlowTestMain',
          params: { organisationId: this.currentOrganisation.id },
        });
      });

    this.$root.$on('bv::modal::show', () => {
      this.editMode = true;
    });
    this.$root.$on('bv::modal::hide', () => {
      this.editMode = false;
    });
  },
  methods: {
    ...mapActions({
      fetchFlowData: 'flows/fetchFlowData',
      getFlowsDefinitions: 'flows/getFlowsDefinitions',
      updateFlowData: 'flows/updateFlowData',
      activateFlowVUEX: 'flows/activateFlow',
      updateFlowName: 'flows/updateFlowName',
      setBlocksCount: 'flows/setBlocksCount',
    }),
    // SB NOTE - inserting text at caret
    insertAtCaret(element, text) {
      const range = document.createRange();
      const selection = window.getSelection();

      const minSelection = Math.min(selection.anchorOffset, selection.focusOffset);
      const maxSelection = Math.max(selection.anchorOffset, selection.focusOffset);

      const beforeHTML = element.innerHTML.substring(0, minSelection).trimStart();
      // SB NOTE strip html tags
      let htmlParser = new DOMParser().parseFromString(beforeHTML, 'text/html');
      let beforeText = htmlParser.body.textContent;
      beforeText = beforeText.replace(/\n/g, '');

      const afterHTML = element.innerHTML.substring(maxSelection, element.innerHTML.length).trimEnd();
      htmlParser = new DOMParser().parseFromString(afterHTML, 'text/html');
      const afterText = htmlParser.body.textContent;

      element.innerText = `${beforeText}${text}${afterText}`;

      range.setStart(element.childNodes[0], minSelection + text.length);
      range.collapse(true);

      selection.removeAllRanges();
      selection.addRange(range);
      element.focus();
    },
    accessDenied() {
      this.forbidden = true;
    },
    activateFlow(state) {
      if (state) {
        this.activateFlowVUEX({ numberId: this.flowData.business_uid, flowId: this.flowData.flow_id, active: true });
      } else {
        this.activateFlowVUEX({ numberId: this.flowData.business_uid, flowId: this.flowData.flow_id, active: false });
      }
    },
    updateFrequency(data) {
      const newFlowData = JSON.parse(JSON.stringify(this.flowData));
      newFlowData.params.trigger_frequency.type = data.type;
      if (data.value) {
        newFlowData.params.trigger_frequency.value = data.value;
      } else {
        delete newFlowData.params.trigger_frequency.value;
      }

      this.updateFlowData(newFlowData);
    },
    proccessFlowDataFrequency(flowData) {
      let label = 'Frequency';
      let icon = null;
      let value = null;
      const frequencyType = flowData.params.trigger_frequency.type;
      switch (frequencyType) {
        case 'with_delay':
          label = this.flowsDefinitionsUI.trigger_frequency[frequencyType].description_template.replace('{{TIME_PLACEHOLDER}}', humanReadableTime(flowData.params.trigger_frequency.value, true));
          icon = this.flowsDefinitionsUI.trigger_frequency[frequencyType].icon;
          value = flowData.params.trigger_frequency.value;
          break;
        case 'once':
          label = this.flowsDefinitionsUI.trigger_frequency[frequencyType].description;
          icon = this.flowsDefinitionsUI.trigger_frequency[frequencyType].icon;
          break;
        case 'every_time':
          label = this.flowsDefinitionsUI.trigger_frequency[frequencyType].description;
          icon = this.flowsDefinitionsUI.trigger_frequency[frequencyType].icon;
          break;
        default:
          break;
      }
      return {
        label,
        icon,
        value,
        noDelete: true,
        dropAbove: false,
        dropBelow: false,
        noMove: true,
        type: 'frequency',
        frequencyType,
      };
    },
    setScene() {
      if (this.emptyObject(this.flowsDefinitionsUI)) {
        setTimeout(() => {
          this.setScene();
        }, 100);
      } else {
        this.$nextTick(() => {
          this.splitBlocksCount = 0;
          this.blocksCount = 0;
          if (this.flowData && this.flowData.blocks && this.flowData.blocks.length > 0) {
            this.title = this.flowData.name;
            const flowFrequency = this.proccessFlowDataFrequency(this.flowData);
            const blocks = this.proccessFlowData(this.flowData.blocks);
            this.proccessedFlowData = {
              ...flowFrequency,
              children: [
                ...blocks,
              ],
            };
            this.flowState = this.flowData.active;
          }
          this.preparingFlow = false;
        });
      }
    },
    /**
     * Converts flow data to tree structure.
     *
     * Params available in the tree structure:
     *    - label: string       --> label of the node
     *    - children: array     --> array of children nodes
     *    - type: string        --> type of the node (split, ???)
     *    - condition: string   --> condition of the node (Condition to print on node header)
     *    - context: string     --> context of the node (message or something to render inside node)
     *    - dropAbove: boolean  --> can drop above this node
     *    - dropBelow: boolean  --> can drop below this node
     *    - noDelete: boolean   --> can delete this node
     *    - noMove: boolean     --> can move this node
     *    - icon: string        --> icon to display on the node
     *    - iconBgColor: string --> background color of the icon
     *    - addAfter: boolean   --> defines if it should be posible to add a new node after this one
     */
    proccessFlowData(blocks) {
      const proccessedBlocks = [];
      let previousBlock = null;
      blocks.forEach((block) => {
        // console.log('BLOCK', block);
        let label = 'Test label';
        let type = 'split';
        let condition = 'Test condition';
        let context = null;
        const id = block.block_id;
        let icon = null;
        let iconBgColor = null;
        let dropAbove = true;
        let noMove = false;
        let noDelete = false;
        let typeSubcode = null;
        const blockData = block;
        delete blockData.blocks;
        let addAfter = true;
        this.blocksCount++;

        try {
          const targetTypeDefinition = this.flowsDefinitions.blocks.definitions[block.category].find(l => l.meta.type === block.type);
          icon = blockData && blockData.block_data && blockData.block_data.avatar ? blockData.block_data.avatar : targetTypeDefinition.definition.icons.dark;
          iconBgColor = targetTypeDefinition.meta.colors.background;
          addAfter = targetTypeDefinition.definition.limits && targetTypeDefinition.definition.limits.is_last_block_in_flow ? !targetTypeDefinition.definition.limits.is_last_block_in_flow : true;
          switch (block.category) {
            case 'triggers':
              label = this.flowsDefinitionsUI.triggers[block.type].options[block.params.triggers[0].type].name;
              type = 'trigger';
              noMove = true;
              noDelete = true;
              dropAbove = false;
              typeSubcode = block.type;
              break;
            case 'actions':
              label = this.flowsDefinitionsUI.actions[block.type].name;
              type = 'action';
              noMove = false;
              noDelete = false;
              typeSubcode = block.type;
              try {
                context = block.params.elements.find(element => element.type === 'text').value;
              } catch (error) {}
              if (block.type === 'ASSAITOCONV') {
                noMove = true;
                // set custom icon based on which assistant is selected
                const aiAgent = this.organisationMembers2[memberTypes.AI_ASSISTANTS].find(
                  member => member.id === block.params.user_uid,
                );
                icon = aiAgent.avatar;
              }
              break;
            case 'logic':
              if (block.type === 'CONREP') {
                label = 'TODO - CONTACT REPLY';
                type = 'logic';
                condition = 'When';
                noMove = true;
              } else if (block.type === 'FILTER') {
                label = 'TODO - filter label';
                type = 'logic';
                condition = 'If';
                noMove = false; // true if direkt SPLIT child - handled inside FlowNode by checking sibling count
              } else if (block.type === 'SPLIT') {
                this.splitBlocksCount++;
              }
              break;
            default:
              break;
          }
        } catch (error) {
          // console.log('NO CONTEXT');
        }

        const newBlock = {
          id,
          label,
          type,
          typeSubcode,
          condition,
          context,
          icon,
          iconBgColor,
          dropAbove,
          dropBelow: true,
          noDelete,
          noMove,
          children: [],
          blockData,
          addAfter,
        };

        if (block.child_flows && block.child_flows.length > 0) {
          block.child_flows.forEach((childFlow) => {
            const childBlocks = this.proccessFlowData(childFlow.blocks);
            newBlock.children = newBlock.children.concat(childBlocks);
          });
        }

        if (previousBlock) {
          previousBlock.children.push(newBlock);
        } else {
          proccessedBlocks.push(newBlock);
        }
        previousBlock = newBlock;
      });

      this.setBlocksCount({
        total: this.blocksCount,
        split: this.splitBlocksCount,
      });

      return proccessedBlocks;
    },
    editTitle(forced = null) {
      if (forced !== null) {
        this.editMode = forced;
      } else {
        this.editMode = !this.editMode;
      }
      if (this.editMode) {
        this.$nextTick(() => {
          this.$refs.editableTitle.focus();
          // this.$refs.editableTitle.setSelectionRange(0, 0);
          const range = document.createRange();
          const selection = window.getSelection();
          range.setStart(this.$refs.editableTitle, this.$refs.editableTitle.childNodes.length);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);

          this.$refs.editableTitle.scrollTo({
            left: 9999999,
            behavior: 'smooth',
          });

          window.addEventListener('keydown', this.handleKeyDown);
        });
      } else {
        window.removeEventListener('keydown', this.handleKeyDown);
      }
    },
    // Sets the selection at the end of the contenteditable element and cleans up a content a bit.
    titleEditFocus(e) {
      setTimeout(() => {
        const titleElement = document.getElementById('title-edit');
        titleElement.innerText = titleElement.innerText;

        const range = document.createRange();
        const selection = window.getSelection();

        range.setStart(e.srcElement.childNodes[0], e.srcElement.innerHTML.length);
        range.collapse(true);

        selection.removeAllRanges();
        selection.addRange(range);
        e.srcElement.focus();
      }, 0);
    },
    titleEditBlur() {
      this.$refs.editableTitle.scrollTo({
        left: 0,
      });
      this.$nextTick(() => {
        this.backBtnDisabled = true;
        this.editTitle(false);
        if (this.title === this.$refs.editableTitle.innerText) {
          this.backBtnDisabled = false;
          return;
        }
        this.title = this.$refs.editableTitle.innerText ? this.$refs.editableTitle.innerText : this.title;
        this.$refs.editableTitle.innerText = this.title;
        this.updateFlowName({ numberId: this.flowData.business_uid, flowId: this.flowData.flow_id, name: this.title });
        setTimeout(() => {
          this.backBtnDisabled = false;
        }, 0);
      });
    },
    handleKeyDown(event) {
      switch (event.keyCode) {
        case 13: // ENTER
          event.preventDefault();
          this.$refs.editableTitle.blur();
          break;
        case 27: // ESC
          this.$refs.editableTitle.innerText = this.title;
          this.$refs.editableTitle.blur();
          break;
        default:
          break;
      }
    },
    tryBack() {
      if (!this.editMode && !this.backBtnDisabled) {
        this.goBack('WAManage');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.settings__title {
  align-items: center !important;
  height: 54px;

  .flow-state-label {
    font-size: 14px;
    font-weight: 400;
    color: $blackUltralight;
    white-space: unset;
    overflow: unset;
    text-overflow: unset;

    &.live {
      color: $bluePrimary;
    }
  }
}

.edit-title {
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
  border: solid 1px $greyBorder;
  background-color: $white;
}

.back-btn {
    min-height: 26px;
    max-width: 100%;
    // min-width: 50%;
    .edit-mode {
      cursor: text;
      text-overflow: unset;
    };

    p {
      min-width: 1px;
    }
  }

.max-height {
  // height: calc(100% - 61px);
  height: calc(100% - 54px);
}

.flow-manage {
  background-color: $white;
  .settings__title {
    padding-top: 0;
    margin: 0 15px;
    width: calc(100% - 30px) !important;
    max-width: calc(100% - 30px) !important;
    .back-btn p {
      font-size: 18px;
    }
  }
}
</style>
